import React from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import { useTranslation } from 'react-i18next';
import Checked from 'src/icons/Checked';
import styles from './DeclarationInfoSection.scss';

const DeclarationInfoSection = ({ declaration, title, maxCode }) => {
  const { t, i18n } = useTranslation();

  const getPeriodDisplay = () => {
    if (declaration.periodType === 'monthly') {
      moment.locale(i18n.language);
      return moment({ year: declaration.periodYear, month: declaration.periodMonth - 1 }).format(t('features.ReportIncomeTax.show.main_form.month_format'));
    }
    return t('features.ReportIncomeTax.show.main_form.quarter_format', { quarter: declaration.periodQuarter, year: declaration.periodYear });
  };

  const isFirstTime = !declaration.supplementaryNo;

  const infoRows = [
    { code: '01', label: t('features.ReportIncomeTax.show.main_form.tax_period'), value: getPeriodDisplay() },
    {
      code: '02',
      label: t('features.ReportIncomeTax.show.main_form.first_time'),
      render: () => (
        <div className={`declaration-info-grid__value ${styles.checkedValue}`}>
          <Checked checked={isFirstTime} />
        </div>
      )
    },
    { code: '03', label: t('features.ReportIncomeTax.show.main_form.supplementary_no'), value: declaration.supplementaryNo },
    { code: '04', label: t('features.ReportIncomeTax.show.main_form.taxpayer_name'), value: declaration.companyName },
    { code: '05', label: t('features.ReportIncomeTax.show.main_form.tax_code'), value: declaration.companyTaxCode },
    { code: '06', label: t('features.ReportIncomeTax.show.main_form.address'), value: declaration.companyAddress },
    { code: '07', label: t('features.ReportIncomeTax.show.main_form.ward_district'), value: declaration.wardDistrict },
    { code: '08', label: t('features.ReportIncomeTax.show.main_form.province_city'), value: declaration.provinceCity },
    { code: '09', label: t('features.ReportIncomeTax.show.main_form.phone'), value: declaration.phone },
    { code: '10', label: t('features.ReportIncomeTax.show.main_form.fax'), value: declaration.fax },
    { code: '11', label: t('features.ReportIncomeTax.show.main_form.email'), value: declaration.email },
    { code: '12', label: t('features.ReportIncomeTax.show.main_form.tax_agent_name'), value: declaration.taxAgentName },
    { code: '13', label: t('features.ReportIncomeTax.show.main_form.tax_agent_code'), value: declaration.taxAgentCode },
    {
      code: '14',
      label: t('features.ReportIncomeTax.show.main_form.tax_agent_contract'),
      render: () => (
        <div className={`declaration-info-grid__value ${styles.contractValue}`}>
          <span>
            {t('features.ReportIncomeTax.show.main_form.contract_no')}: {declaration.taxAgentContractNo || '—'}
          </span>
          <span>
            {t('features.ReportIncomeTax.show.main_form.contract_date')}: {declaration.taxAgentContractDate || '—'}
          </span>
        </div>
      )
    },
    {
      code: '15',
      label: t('features.ReportIncomeTax.show.main_form.tax_allocation'),
      render: () => (
        <div className={`declaration-info-grid__value ${styles.checkedValue}`}>
          <Checked checked={Boolean(declaration.ct15HasMultiProvinceAlloc)} />
        </div>
      )
    }
  ];

  return (
    <div className="declaration-form-section-wrapper">
      <div className="declaration-form-section">
        <div className="declaration-form-section__header">{title}</div>
        <div className="declaration-form-section__body declaration-form-section__body--info">
          <div className="declaration-info-grid">
            {infoRows.filter(row => !maxCode || row.code <= maxCode).map(row => (
              <div key={row.code} className="declaration-info-grid__row">
                <div className="declaration-info-grid__label">
                  [{row.code}] {row.label}
                </div>
                {row.render ? row.render() : <div className="declaration-info-grid__value">{row.value || '—'}</div>}
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

DeclarationInfoSection.propTypes = {
  declaration: PropTypes.shape({
    periodType: PropTypes.string,
    periodYear: PropTypes.number,
    periodMonth: PropTypes.number,
    periodQuarter: PropTypes.number,
    supplementaryNo: PropTypes.number,
    companyName: PropTypes.string,
    companyTaxCode: PropTypes.string,
    companyAddress: PropTypes.string,
    wardDistrict: PropTypes.string,
    provinceCity: PropTypes.string,
    phone: PropTypes.string,
    fax: PropTypes.string,
    email: PropTypes.string,
    taxAgentName: PropTypes.string,
    taxAgentCode: PropTypes.string,
    taxAgentContractNo: PropTypes.string,
    taxAgentContractDate: PropTypes.string,
    ct15HasMultiProvinceAlloc: PropTypes.bool
  }).isRequired,
  title: PropTypes.string.isRequired,
  maxCode: PropTypes.string
};

DeclarationInfoSection.defaultProps = {
  maxCode: null
};

export default DeclarationInfoSection;
